"use client";

import { useState } from "react";

import Image from "next/image";

import { ChevronDown, ChevronRight, ChevronUp } from "lucide-react";

import flightTickIcon from "@repo/ui/assets/HomePage/flight-tick.svg";
import { Button } from "@repo/ui/components/ui/button";
import { usePageContext } from "@repo/ui/contexts/PageContext";
import type { HomePageContent } from "@repo/ui/types/cms";

import type { NormalizedHomePageSections } from "@/features/home/helpers/normalizeHomePageSections";

export default function Faq() {
  // ⚙️ CMS content
  const { normalizedSections } = usePageContext<HomePageContent, NormalizedHomePageSections>();
  const { faqs, imageTextHeader } = normalizedSections;

  const [showAll, setShowAll] = useState(false);

  const cardBlocks = faqs?.cardBlocks || [];
  const visibleCards = showAll ? cardBlocks : cardBlocks.slice(0, 3);

  if (!cardBlocks || cardBlocks.length === 0) {
    return null;
  }

  return (
    <section
      className="responsive-padding responsive-layout w-full text-center"
      data-testid="faq-section"
    >
      <div className="mx-auto max-w-6xl">
        <div className="border-brand-cyan border-l-4 py-2 pl-6 mb-6 max-sm:mb-4 max-sm:pb-5">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex-1">
              <div className="mb-2 flex items-center space-x-1">
                <Image
                  src={imageTextHeader?.iconUrl ?? flightTickIcon}
                  width={28}
                  height={28}
                  alt="Flight Logo"
                />
                <h2
                  className="text-left text-lg sm:text-3xl font-semibold leading-[36px] [&>p]:m-0"
                  dangerouslySetInnerHTML={{ __html: imageTextHeader?.title ?? "" }}
                />
              </div>
              <p
                className="text-left text-black/60 text-base sm:text-xl leading-[28px] max-sm:pl-7 [&>p]:m-0"
                dangerouslySetInnerHTML={{ __html: imageTextHeader?.subtitle ?? "" }}
              />
            </div>
            <div className="flex justify-start sm:justify-end sm:mt-0 max-sm:pl-7">
              <Button
                className="group hover:border-brand-cyan hover:text-brand-cyan flex items-center gap-2 rounded-full border-2 border-black bg-white px-5 py-2 text-sm text-black transition-colors duration-600"
                variant="outline"
              >
                <span>{imageTextHeader?.ctaText}</span>
                <span className="group-hover:border-brand-cyan flex h-5 w-5 items-center justify-center rounded-full border border-black transition-colors duration-600">
                  <ChevronRight className="h-3 w-3" />
                </span>
              </Button>
            </div>
          </div>
        </div>

        <div className="grid gap-6 rounded-xl bg-white p-2 text-left sm:p-5 md:grid-cols-3">
          {visibleCards.map((card, index) => (
            <div className="flex flex-col justify-between" key={card.codename || index}>
              {card.image && (
                <div className="text-4xl">
                  <Image src={card.image} width={45} height={45} alt={card.title || "Icon"} />
                </div>
              )}
              {card.title && (
                <h3
                  className="mb-2 text-lg font-bold text-sky-900 [&>p]:m-0"
                  dangerouslySetInnerHTML={{ __html: card.title }}
                />
              )}

              {card.description && (
                <p
                  className="mb-4 text-sm text-black/75 [&>p]:m-0"
                  dangerouslySetInnerHTML={{ __html: card.description }}
                />
              )}

              {(card.link || card.link_text || card.linkText) && (
                <a
                  href={card.link || "#"}
                  className="text-brand-cyan hover:text-brand-blue text-sm font-medium hover:underline"
                >
                  {card.link_text || card.linkText}
                </a>
              )}
            </div>
          ))}
          {cardBlocks.length > 3 && (
            <div className="col-span-full flex justify-center">
              <Button
                variant="ghost"
                className="hover:text-brand-blue flex items-center text-gray-600 hover:underline"
                onClick={() => setShowAll(!showAll)}
              >
                {showAll ? faqs?.showLessText || "Show less" : faqs?.showMoreText || "Show more"}
                {showAll ? (
                  <ChevronUp className="ml-2 h-4 w-4" />
                ) : (
                  <ChevronDown className="ml-2 h-4 w-4" />
                )}
              </Button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
